import { classSkeleton } from "./classfile";
import type { CfrConfig, CfrOptions } from "./cfr-browser";
import type { ClassInfo } from "./types";

const MAX_SOURCE_BYTES = 2 * 1024 * 1024;
const MAX_READABLE_LINE = 900;

export interface SourceQuality {
  score: number;
  replacementCharacters: number;
  controlCharacters: number;
  giantLines: number;
  decompilerNoise: number;
  longestLine: number;
  balancedBraces: boolean;
  looksLikeJava: boolean;
}

export interface SourceRecoveryResult {
  engine: "CFR" | "Vineflower" | "Structural";
  raw: string;
  recovered: string;
  profile: string;
  attempts: number;
  quality: SourceQuality;
  fallback: boolean;
  changes: string[];
  errors: string[];
}

const CFR_PROFILES: Array<{ name: string; options: CfrOptions }> = [
  {
    name: "Balanced recovery",
    options: {
      comments: "false",
      hidelangimports: "false",
      decodeenumswitch: "true",
      decodestringswitch: "true",
      renameillegalidents: "true",
      renameenumidents: "true",
      removeboilerplate: "true",
      stringbuilder: "true",
    },
  },
  {
    name: "Aggressive control-flow recovery",
    options: {
      comments: "true",
      hidelangimports: "false",
      decodeenumswitch: "true",
      decodestringswitch: "true",
      renameillegalidents: "true",
      renameenumidents: "true",
      removeboilerplate: "true",
      recover: "true",
      forcetopsort: "true",
      forcetopsortaggress: "true",
      forloopaggcapture: "true",
      stringbuilder: "true",
    },
  },
  {
    name: "Conservative structural recovery",
    options: {
      comments: "true",
      hidelangimports: "false",
      decodeenumswitch: "false",
      decodestringswitch: "false",
      renameillegalidents: "true",
      renameenumidents: "true",
      removeboilerplate: "false",
      recover: "false",
      forcetopsort: "false",
      stringbuilder: "false",
    },
  },
];

function controlCharacterCount(value: string) {
  let count = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if ((code < 0x09 || (code > 0x0d && code < 0x20)) && code !== 0) count += 1;
  }
  return count;
}

export function scoreSourceQuality(source: string): SourceQuality {
  const lines = source.split(/\r?\n/);
  const longestLine = lines.reduce(
    (maximum, line) => Math.max(maximum, line.length),
    0,
  );
  const giantLines = lines.filter((line) => line.length > MAX_READABLE_LINE).length;
  const decompilerNoise =
    (source.match(/\bcfr_ignored(?:_\d+)?\b/g) ?? []).length +
    Math.floor((source.match(/\/\*\s*synthetic\s*\*\//gi) ?? []).length / 3);
  const replacementCharacters = (source.match(/\ufffd/g) ?? []).length;
  const controlCharacters = controlCharacterCount(source);
  const openBraces = (source.match(/\{/g) ?? []).length;
  const closeBraces = (source.match(/\}/g) ?? []).length;
  const balancedBraces = openBraces === closeBraces && openBraces > 0;
  const looksLikeJava =
    /\b(?:class|interface|enum|record)\s+[\p{L}_$][\p{L}\p{N}_$]*/u.test(source) &&
    /[;{}]/.test(source);
  let score = 100;
  if (!looksLikeJava) score -= 45;
  if (!balancedBraces) score -= 18;
  score -= Math.min(30, replacementCharacters * 2);
  score -= Math.min(30, controlCharacters * 3);
  score -= Math.min(28, giantLines * 4);
  score -= Math.min(24, decompilerNoise * 2);
  if (longestLine > 20_000) score -= 18;
  if (source.length < 24) score -= 45;
  if (source.length > MAX_SOURCE_BYTES) score -= 12;
  return {
    score: Math.max(0, Math.min(100, score)),
    replacementCharacters,
    controlCharacters,
    giantLines,
    decompilerNoise,
    longestLine,
    balancedBraces,
    looksLikeJava,
  };
}

function sideEffectFreeOpaqueExpression(value: string) {
  const expression = value.trim();
  if (
    !expression ||
    /["'`.[\]{};]/.test(expression) ||
    /\bnew\b/.test(expression) ||
    /[A-Za-z_$][\w$]*\s*\(/.test(expression) ||
    /\+\+|--|=(?!=)/.test(expression)
  ) {
    return false;
  }
  return /^[\w\s$()+\-*/%^|&~<>!?:]+$/.test(expression);
}

function identifierOccurrences(source: string, identifier: string) {
  const escaped = identifier.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return (source.match(new RegExp(`(?<![\\w$])${escaped}(?![\\w$])`, "g")) ?? [])
    .length;
}

function removeProvenDeadStatements(source: string) {
  let lines = source.split(/\r?\n/);
  let removed = 0;
  for (let pass = 0; pass < 4; pass += 1) {
    const current = lines.join("\n");
    let passRemoved = 0;
    lines = lines.filter((line) => {
      const ignored = line.match(
        /^\s*(?:final\s+)?(?:byte|short|int|long|float|double|boolean|char)\s+cfr_ignored(?:_\d+)?\s*=\s*(.+);\s*$/,
      );
      if (ignored && sideEffectFreeOpaqueExpression(ignored[1])) {
        passRemoved += 1;
        return false;
      }
      const local = line.match(
        /^\s*(?:final\s+)?(?:byte|short|int|long|float|double|boolean|char|String|Object)\s+([A-Za-z_$][\w$]*)\s*=\s*(.+);\s*$/,
      );
      if (
        local &&
        identifierOccurrences(current, local[1]) === 1 &&
        sideEffectFreeOpaqueExpression(local[2])
      ) {
        passRemoved += 1;
        return false;
      }
      return true;
    });
    removed += passRemoved;
    if (!passRemoved || removed >= 5_000) break;
  }
  return {
    source: lines.join("\n").replace(/\n{4,}/g, "\n\n\n"),
    removed,
  };
}

function removeUnusedSyntheticFields(source: string) {
  const lines = source.split(/\r?\n/);
  let removed = 0;
  const cleaned = lines.filter((line) => {
    const field = line.match(
      /^\s*(?:(?:public|protected|private|static|final|volatile|transient|synthetic)\s+)*(?:[\w$.[\]<>?,]+\s+)(bv\$[\w$]+)\s*(?:=\s*(.+))?;\s*$/,
    );
    if (!field) return true;
    const initializer = field[2]?.trim();
    if (
      identifierOccurrences(source, field[1]) !== 1 ||
      (initializer && !sideEffectFreeOpaqueExpression(initializer))
    ) {
      return true;
    }
    removed += 1;
    return false;
  });
  return {
    source: cleaned.join("\n").replace(/\n{4,}/g, "\n\n\n"),
    removed,
  };
}

function compactOpaqueLine(line: string) {
  if (line.length <= MAX_READABLE_LINE) return null;
  const indent = line.match(/^\s*/)?.[0] ?? "";
  const head = line.trimStart().slice(0, 220).replaceAll("*/", "* /");
  const tail = line.slice(-140).replaceAll("*/", "* /");
  return `${indent}/* JarLab collapsed ${line.length.toLocaleString()} characters of opaque expression.\n${indent} * Start: ${head}\n${indent} * End: ${tail}\n${indent} */`;
}

function stripControlCharacters(source: string) {
  return Array.from(source, (character) => {
    const code = character.charCodeAt(0);
    return (code < 0x09 || (code > 0x0d && code < 0x20)) && code !== 0
      ? `\\u${code.toString(16).padStart(4, "0")}`
      : character;
  }).join("");
}

export function recoverReadableSource(
  rawSource: string,
  classInfo: ClassInfo,
  engineLabel = "CFR",
): Pick<SourceRecoveryResult, "recovered" | "fallback" | "changes"> {
  const changes: string[] = [];
  let source = rawSource.slice(0, MAX_SOURCE_BYTES);
  if (rawSource.length > source.length) {
    changes.push(
      `Limited ${engineLabel} output to ${(MAX_SOURCE_BYTES / 1024 / 1024).toFixed(0)} MB`,
    );
  }

  const controls = controlCharacterCount(source);
  if (controls) {
    source = stripControlCharacters(source);
    changes.push(`Escaped ${controls.toLocaleString()} control characters`);
  }

  const deadStatements = removeProvenDeadStatements(source);
  source = deadStatements.source;
  if (deadStatements.removed) {
    changes.push(
      `Removed ${deadStatements.removed.toLocaleString()} proven-dead CFR arithmetic statements`,
    );
  }

  const syntheticFields = removeUnusedSyntheticFields(source);
  source = syntheticFields.source;
  if (syntheticFields.removed) {
    changes.push(
      `Removed ${syntheticFields.removed.toLocaleString()} unreferenced synthetic field declaration${syntheticFields.removed === 1 ? "" : "s"}`,
    );
  }

  let collapsed = 0;
  source = source
    .split(/\r?\n/)
    .map((line) => {
      const compacted = compactOpaqueLine(line);
      if (compacted) collapsed += 1;
      return compacted ?? line.replace(/[ \t]+$/g, "");
    })
    .join("\n");
  if (collapsed) {
    changes.push(`Collapsed ${collapsed.toLocaleString()} oversized opaque lines`);
  }

  const repeatedSynthetic = /(?:\/\*\s*synthetic\s*\*\/\s*){3,}/gi;
  if (repeatedSynthetic.test(source)) {
    source = source.replace(
      repeatedSynthetic,
      "/* JarLab collapsed repeated synthetic markers */ ",
    );
    changes.push("Collapsed repeated synthetic markers");
  }

  const quality = scoreSourceQuality(source);
  if (quality.score < 28) {
    changes.push(
      `Rejected unreadable ${engineLabel} output and used structural source`,
    );
    return {
      recovered: [
        "/*",
        ` * ${engineLabel} produced output that failed JarLab's readability checks.`,
        " * The raw result remains available in the Raw source tab.",
        " */",
        classSkeleton(classInfo),
      ].join("\n"),
      fallback: true,
      changes,
    };
  }

  if (changes.length) {
    source = [
      "/* JarLab readable-source recovery",
      ...changes.map((change) => ` * ${change}`),
      ` * Raw ${engineLabel} output is preserved in the adjacent tab.`,
      " */",
      "",
      source,
    ].join("\n");
  }
  return { recovered: source, fallback: false, changes };
}

async function runVineflower(
  internalName: string,
  classInfo: ClassInfo,
  source: CfrConfig["source"],
  nestedClasses: readonly string[] = [],
): Promise<SourceRecoveryResult> {
  if (!source) {
    throw new Error("Vineflower requires a class source loader.");
  }
  const { decompileClassWithVineflower } = await import("./vineflower-browser");
  const result = await decompileClassWithVineflower(
    internalName,
    source,
    nestedClasses,
  );
  const quality = scoreSourceQuality(result.source);
  const readable = recoverReadableSource(result.source, classInfo, "Vineflower");
  return {
    engine: "Vineflower",
    raw: result.source,
    recovered: readable.recovered,
    profile:
      result.runtime === "JavaScript"
        ? "Vineflower recovery · JavaScript runtime"
        : "Vineflower recovery",
    attempts: 1,
    quality,
    fallback: readable.fallback,
    changes: readable.changes,
    errors: [],
  };
}

export async function decompileClassResilient(
  internalName: string,
  classInfo: ClassInfo,
  config: Pick<CfrConfig, "source"> & {
    engine?: "cfr" | "vineflower";
    // Internal names of the target's inner and anonymous classes. CFR finds
    // them through the source loader; Vineflower has to be told.
    nestedClasses?: readonly string[];
  },
  // Each engine backs the other up once. This stops a failing pair from
  // bouncing between them.
  allowOtherEngine = true,
): Promise<SourceRecoveryResult> {
  if (config.engine === "vineflower") {
    try {
      return await runVineflower(
        internalName,
        classInfo,
        config.source,
        config.nestedClasses,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Vineflower failed";
      if (!allowOtherEngine) throw error;
      const cfrFallback = await decompileClassResilient(
        internalName,
        classInfo,
        { source: config.source, engine: "cfr" },
        false,
      );
      return {
        ...cfrFallback,
        profile: `Vineflower fallback → ${cfrFallback.profile}`,
        attempts: cfrFallback.attempts + 1,
        fallback: true,
        changes: [
          "Vineflower failed, so JarLab recovered this class with CFR",
          ...cfrFallback.changes,
        ],
        errors: [`Vineflower: ${message}`, ...cfrFallback.errors],
      };
    }
  }
  const { decompileClass } = await import("./cfr-browser");
  const errors: string[] = [];
  let best:
    | {
        raw: string;
        profile: string;
        quality: SourceQuality;
      }
    | undefined;
  let attempts = 0;
  let engineUnavailable = false;

  for (const profile of CFR_PROFILES) {
    attempts += 1;
    try {
      const raw = await decompileClass(internalName, {
        ...config,
        options: profile.options,
      });
      const quality = scoreSourceQuality(raw);
      if (!best || quality.score > best.quality.score) {
        best = { raw, profile: profile.name, quality };
      }
      if (quality.score >= 84 && quality.giantLines === 0) break;
    } catch (error) {
      errors.push(
        `${profile.name}: ${
          error instanceof Error ? error.message : "Decompiler failed"
        }`,
      );
      // A profile that CFR rejects is worth retrying under a different one.
      // An engine that never started is not.
      if (error instanceof Error && error.name === "CfrUnavailableError") {
        engineUnavailable = true;
        break;
      }
    }
  }

  if (!best && allowOtherEngine && config.source) {
    try {
      const vineflower = await runVineflower(
        internalName,
        classInfo,
        config.source,
        config.nestedClasses,
      );
      return {
        ...vineflower,
        profile: `CFR fallback → ${vineflower.profile}`,
        attempts: attempts + 1,
        fallback: true,
        changes: [
          engineUnavailable
            ? "CFR could not start in this browser, so JarLab recovered this class with Vineflower"
            : "CFR failed under every bounded recovery profile, so JarLab recovered this class with Vineflower",
          ...vineflower.changes,
        ],
        errors,
      };
    } catch (error) {
      errors.push(
        `Vineflower: ${
          error instanceof Error ? error.message : "Vineflower failed"
        }`,
      );
    }
  }

  if (!best) {
    const recovered = classSkeleton(classInfo);
    return {
      engine: "Structural",
      raw: "",
      recovered,
      profile: "Structural fallback",
      attempts,
      quality: scoreSourceQuality(recovered),
      fallback: true,
      changes: [
        engineUnavailable
          ? "No decompiler engine could start in this browser"
          : "Every bounded recovery profile failed",
      ],
      errors,
    };
  }

  const readable = recoverReadableSource(best.raw, classInfo);
  return {
    engine: "CFR",
    raw: best.raw,
    recovered: readable.recovered,
    profile: best.profile,
    attempts,
    quality: best.quality,
    fallback: readable.fallback,
    changes: readable.changes,
    errors,
  };
}
